import React, { useState, useEffect, createContext, useContext, useRef } from 'react';
import { translations } from './Data/Translation/translations'; 
import Hero from './Components/Pages/Hero';
import WhyUs from './Components/Pages/WhyUs'; 
import AboutUs from './Components/Pages/AboutUs';
import OurServices from './Components/Pages/OurServices';
import OurFeatures from './Components/Pages/OurFeatures';
import Footer from './Components/Pages/Footer';

// 1. إدارة اللغة
const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) throw new Error('useLanguage must be used within a LanguageProvider');
  return context;
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(localStorage.getItem('language') || 'ar');
  
  useEffect(() => {
    document.documentElement.dir = language === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = language;
    document.documentElement.style.scrollBehavior = 'auto';
  }, [language]);

  const changeLanguage = (lang) => {
    setLanguage(lang);
    localStorage.setItem('language', lang);
  };

  const t = (key) => {
    const keys = key.split('.');
    let result = translations[language]; 
    for (const k of keys) {
      if (result && result[k] !== undefined) result = result[k];
      else return key;
    }
    return result;
  };

  return (
    <LanguageContext.Provider value={{ language, changeLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

// 3. مكون ظهور الأقسام
const RevealSection = ({ children, id }) => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => { if (sectionRef.current) observer.unobserve(sectionRef.current); };
  }, []);

  return (
    <div id={id} ref={sectionRef} className={`transition-all duration-[1000ms] ease-out transform ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
      {children}
    </div>
  );
};

// 4. زر العودة للأعلى
const ScrollToTop = () => {
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const toggleVisibility = () => setIsVisible(window.pageYOffset > 400);
    window.addEventListener("scroll", toggleVisibility);
    return () => window.removeEventListener("scroll", toggleVisibility);
  }, []);

  return (
    <button 
      onClick={() => {
        const startPosition = window.pageYOffset;
        const distance = -startPosition;
        let startTime = null;
        const duration = 800;
        function animation(currentTime) {
          if (startTime === null) startTime = currentTime;
          const timeElapsed = currentTime - startTime;
          const run = (t, b, c, d) => { t /= d; return -c * t * (t - 2) + b; };
          window.scrollTo(0, run(timeElapsed, startPosition, distance, duration));
          if (timeElapsed < duration) requestAnimationFrame(animation);
        }
        requestAnimationFrame(animation);
      }} 
      className={`fixed bottom-8 right-8 z-[1000] p-4 rounded-full bg-[#1A4D90] text-white shadow-2xl transition-all duration-500 ${isVisible ? "opacity-100 scale-100" : "opacity-0 scale-0 pointer-events-none"}`}
    >
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 10l7-7m0 0l7 7m-7-7v18" /></svg>
    </button>
  );
};

// 5. المكون الرئيسي
function App() {
  return (
    <LanguageProvider>
      <div className="App bg-[#030B15] overflow-x-hidden">
        
        {/* زر واتساب المطور مع تأثير النبض */}
        <div className="fixed bottom-8 left-8 z-[1000] flex items-center justify-center">
          {/* طبقات النبض الخارجية */}
          <span className="absolute inline-flex h-16 w-16 animate-ping rounded-full bg-[#25D366] opacity-75"></span>
          <span className="absolute inline-flex h-12 w-12 animate-pulse rounded-full bg-[#25D366] opacity-50"></span>
          
          {/* الزر الفعلي */}
          <a 
            href="https://wa.me/963943523797" 
            target="_blank" 
            rel="noreferrer" 
            className="relative w-[60px] h-[60px] rounded-full bg-[#25D366] flex items-center justify-center shadow-lg transform hover:scale-110 transition-transform z-10"
          >
            <svg viewBox="0 0 448 512" className="w-7 h-7 text-white" fill="currentColor">
              <path d="M380.9 97.1C339 55.1 283.2 32 223.9 32c-122.4 0-222 99.6-222 222 0 39.1 10.2 77.3 29.6 111L0 480l117.7-30.9c32.4 17.7 68.9 27 106.1 27h.1c122.3 0 224.1-99.6 224.1-222 0-59.3-25.2-115-67.1-157zm-157 341.6c-33.2 0-65.7-8.9-94-25.7l-6.7-4-69.8 18.3L72 359.2l-4.4-7c-18.5-29.4-28.2-63.3-28.2-98.2 0-101.7 82.8-184.5 184.6-184.5 49.3 0 95.6 19.2 130.4 54.1 34.8 34.9 56.2 81.2 56.1 130.5 0 101.8-84.9 184.6-186.6 184.6zm101.2-138.2c-5.5-2.8-32.8-16.2-37.9-18-5.1-1.9-8.8-2.8-12.5 2.8-3.7 5.6-14.3 18-17.6 21.8-3.2 3.7-6.5 4.2-12 1.4-5.5-2.8-23.2-8.5-44.2-27.1-16.4-14.6-27.4-32.7-30.6-38.2-3.2-5.6-.3-8.6 2.4-11.4 2.5-2.5 5.6-6.5 8.3-9.8 2.8-3.3 3.7-5.6 5.6-9.3 1.8-3.7.9-6.9-.5-9.8-1.4-2.8-12.5-30.1-17.1-41.2-4.5-10.8-9.1-9.3-12.5-9.5-3.2-.2-6.9-.2-10.6-.2-3.7 0-9.7 1.4-14.8 6.9-5.1 5.6-19.4 19-19.4 46.3 0 27.3 19.9 53.7 22.6 57.4 2.8 3.7 39.1 59.7 94.8 83.8 13.2 5.8 23.5 9.2 31.5 11.8 13.3 4.2 25.4 3.6 35 2.2 10.7-1.6 32.8-13.4 37.4-26.4 4.6-13 4.6-24.1 3.2-26.4-1.3-2.5-5-3.9-10.5-6.6z"/>
            </svg>
          </a>
        </div>

        <ScrollToTop />
        
        <section id="home">
          <Hero />
        </section>

        <RevealSection id="about">
          <AboutUs />
        </RevealSection>

        <RevealSection id="services">
          <OurServices />
        </RevealSection>

        <RevealSection id="features">
          <OurFeatures />
        </RevealSection>

        <RevealSection id="why-us">
          <WhyUs />
        </RevealSection>

        <RevealSection id="contact">
          <Footer />
        </RevealSection>

        
      </div>
    </LanguageProvider>
  );
}

export default App;